/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { UnitOfMeasure } from 'utils/enums';
import SalesInvoiceActions from 'utils/SalesInvoiceActions';
import { toastConfig } from 'utils/toastConfig';
import {
  TPaymentMethod,
  TSalesInvoice,
  TSalesInvoiceItems,
  TUser
} from 'utils/types';
import useDebounce from 'hooks/useDebounce';
import EditSalesInvoiceInputs from './SalesInvoice/EditSalesInvoiceInputs';
import EditSalesInvoiceTable from './SalesInvoice/EditSalesInvoiceTable';
import {
  buildUpsertSalesInvoiceItemPayload,
  mapApiSalesInvoiceItems
} from './SalesInvoice/salesInvoiceItemPayload';

export type EditSalesInvoice = {
  soldTo: string | null;
  customerId: number | null;
  customerName: string | null;
  invoicedOn: Date | string | null;
  invoiceNumber: string | null;
  remarks: string | null;
  paymentMethodId: number | null;
  paymentReference: string | null;
  salesStaffId: number | null;
  discountCode: string | null;
  discountCodeUsed: string | null;
};

const EditSalesInvoiceForm = () => {
  const { id } = useParams();
  const [salesInvoice, setSalesInvoice] = useState<EditSalesInvoice>({
    soldTo: null,
    customerId: null,
    customerName: '',
    invoicedOn: new Date(),
    invoiceNumber: '',
    remarks: '',
    paymentMethodId: null,
    paymentReference: '',
    salesStaffId: null,
    discountCode: null,
    discountCodeUsed: null
  });
  const [items, setItems] = useState<TSalesInvoiceItems[]>([
    {
      productId: 0,
      procurementInvoiceId: 0,
      quantity: 1,
      unitOfMeasure: UnitOfMeasure.Piece,
      unitPrice: 0,
      productName: '',
      total: 0,
      salesInvoiceItemId: 0,
      noIdProductName: '',
      id: '',
      discountApplied: '',
      discountName: '',
      discountedUnitPrice: 0,
      discountedAmount: 0,
      isDiscountable: false
    }
  ]);
  const { selectedBranch } = useBranchContext();
  const [isSalesInvoiceCreated, setIsSalesInvoiceCreated] = useState(false);
  const [salesInvoiceId, setSalesInvoiceId] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<TPaymentMethod[] | null>(
    null
  );
  const [salesStaff, setSalesStaff] = useState<TUser[]>([]);
  const [submitTrigger, setSubmitTrigger] = useState<number | null>(null);
  const [isSubmittingItems, setIsSubmittingItems] = useState(false);
  const [salesInvoiceDetails, setSalesInvoiceDetails] =
    useState<TSalesInvoice | null>(null);
  const navigate = useNavigate();
  const debouncedBranchDomain = useDebounce(selectedBranch?.branchDomain, 300);

  const getPaymentMethods = async () => {
    try {
      const res = await axiosInstance.get(`/api/v1/paymentMethods`);
      setPaymentMethods(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getSalesStaff = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/users?role=1`
      );
      setSalesStaff(res.data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  const handleSalesFormChange = <K extends keyof EditSalesInvoice>(
    field: K,
    value: EditSalesInvoice[K]
  ) => {
    setSalesInvoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitSalesInvoiceItems = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmittingItems) {
      console.log('Already submitting items, skipping...');
      return;
    }

    setIsSubmittingItems(true);
    setIsLoading(true);

    if (!selectedBranch?.branchDomain) {
      toast.error('Branch domain is missing');
      setIsLoading(false);
      setIsSubmittingItems(false);
      return;
    }

    const itemsToSubmit = items.filter(
      item => !item.isSubmitted && (item.productId || item.noIdProductName)
    );

    if (itemsToSubmit.length === 0) {
      setIsLoading(false);
      setIsSubmittingItems(false);
      return;
    }

    try {
      for (const item of itemsToSubmit) {
        const res = await axiosInstance.post(
          `/api/v1/${selectedBranch.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems`,
          buildUpsertSalesInvoiceItemPayload(item)
        );
        const { salesInvoiceItemId } = res.data;
        setItems(prevItems => {
          const updatedItems = prevItems.map(i =>
            i.id === item.id
              ? {
                  ...i,
                  isSubmitted: true,
                  salesInvoiceItemId
                }
              : i
          );
          return updatedItems;
        });
      }
      toast.success('Sales Invoice Items Added', {
        ...toastConfig,
        autoClose: 2000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.errorMessage || 'Something went wrong';
        toast.error(errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setIsLoading(false);
      setIsSubmittingItems(false);
    }
  };

  const saveEditedInvoice = async () => {
    if (!isSalesInvoiceCreated) return;

    const formattedSalesInvoice = {
      soldTo: salesInvoice.soldTo,
      customerId: salesInvoice.customerId,
      invoicedOn: salesInvoice.invoicedOn,
      invoiceNumber: salesInvoice.invoiceNumber,
      remarks: salesInvoice.remarks,
      paymentMethodId: salesInvoice.paymentMethodId,
      paymentReference: salesInvoice.paymentReference,
      salesStaffId: salesInvoice.salesStaffId
    };

    try {
      const res = await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}`,
        formattedSalesInvoice
      );
      
      // Update discount code if it's in the response
      if (res.data.discountCode) {
        setSalesInvoice(prev => ({
          ...prev,
          discountCode: res.data.discountCode
        }));
      }
      
      toast.success('Sales Invoice Updated', {
        ...toastConfig,
        autoClose: 2000
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
        setIsLoading(false);
      }
    }
  };

  const handleSubmitSales = async () => {
    setIsLoading(true);
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}/submit`
      );
      toast.success('Sales Invoice Submitted', {
        ...toastConfig,
        autoClose: 2000
      });
      navigate(`/sales-details/${salesInvoiceId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage =
          error.response?.data?.errorMessage || 'Something went wrong';
        toast.error(errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
        setIsLoading(false);
      }
    }
  };

  const getSalesInvoiceDetails = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${id}`
      );
      const { data } = res;
      handleSalesFormChange('soldTo', data.soldTo);
      handleSalesFormChange('customerId', data.customerId);
      handleSalesFormChange('invoicedOn', data.invoicedOn);
      handleSalesFormChange('invoiceNumber', data.invoiceNumber);
      handleSalesFormChange('remarks', data.remarks);
      handleSalesFormChange('paymentMethodId', data.paymentMethodId);
      handleSalesFormChange('paymentReference', data.paymentReference);
      handleSalesFormChange('salesStaffId', data.salesStaffId);
      handleSalesFormChange('discountCodeUsed', data.discountCodeUsed);
      handleSalesFormChange('discountCode', data.discountCode || '');
      setSalesInvoiceId(data.salesInvoiceId);
      setIsSalesInvoiceCreated(true);
      setSalesInvoiceDetails(data);
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  const getSalesInvoiceItems = async () => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${id}/invoiceItems`
      );
      const apiItems = res.data;
      
      // If no items, add a default empty item
      if (!apiItems || apiItems.length === 0) {
        setItems([
          {
            productId: 0,
            procurementInvoiceId: 0,
            quantity: 1,
            unitOfMeasure: UnitOfMeasure.Piece,
            unitPrice: 0,
            productName: '',
            total: 0,
            salesInvoiceItemId: 0,
            noIdProductName: '',
            id: '',
            discountApplied: '',
            discountName: '',
            discountedUnitPrice: 0,
            discountedAmount: 0,
            isDiscountable: false
          }
        ]);
        return;
      }

      setItems(mapApiSalesInvoiceItems(apiItems));
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  useEffect(() => {
    getPaymentMethods();
  }, []);

  useEffect(() => {
    getSalesStaff();
    getSalesInvoiceDetails();
    getSalesInvoiceItems();
  }, [debouncedBranchDomain]);

  useEffect(() => {
    if (submitTrigger !== null) {
      // Add a small delay to debounce rapid successive triggers
      const timeoutId = setTimeout(() => {
        if (submitTrigger === -1) {
          // Special value to indicate refresh only (no resubmission)
          getSalesInvoiceItems();
        } else {
          // Regular submission of new items
          handleSubmitSalesInvoiceItems();
        }
        setSubmitTrigger(null);
      }, 300); // 300ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [submitTrigger]);

  // Remove the duplicate useEffect for fetching items
  useEffect(() => {
    if (isSalesInvoiceCreated && salesInvoiceId && selectedBranch?.branchDomain) {
      const fetchInvoiceItems = async () => {
        await getSalesInvoiceItems();
      };
      
      // Add a small delay to ensure that the previous save operation has completed
      const timeoutId = setTimeout(() => {
        fetchInvoiceItems();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [salesInvoice.customerId, salesInvoice.soldTo, salesInvoice.discountCode, salesInvoiceId, isSalesInvoiceCreated, selectedBranch]);

  return (
    <>
      <div className="d-flex flex-column">
        <div className="align-self-end">
          <SalesInvoiceActions
            salesInvoiceDetails={salesInvoiceDetails}
            branchDomain={selectedBranch?.branchDomain}
            refetch={getSalesInvoiceDetails}
            salesInvoiceId={id}
          />
        </div>

        <Form className="mb-3">
          <EditSalesInvoiceInputs
            salesInvoice={salesInvoice}
            salesInvoiceDetails={salesInvoiceDetails}
            setSalesInvoice={setSalesInvoice}
            salesStaff={salesStaff}
            paymentMethods={paymentMethods}
            isSalesInvoiceCreated={isSalesInvoiceCreated}
            saveEditedInvoice={saveEditedInvoice}
            setSubmitTrigger={setSubmitTrigger}
            salesInvoiceId={salesInvoiceId}
            getSalesInvoiceItems={getSalesInvoiceItems}
          />
        </Form>
      </div>
      <EditSalesInvoiceTable
        items={items}
        setItems={setItems}
        salesInvoiceId={salesInvoiceId}
        isSalesInvoiceCreated={isSalesInvoiceCreated}
        isLoading={isLoading}
        handleSubmitSales={handleSubmitSales}
        setSubmitTrigger={setSubmitTrigger}
        getSalesInvoiceItems={getSalesInvoiceItems}
        isSubmittingItems={isSubmittingItems}
      />
    </>
  );
};

export default EditSalesInvoiceForm;
